import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import PublicLayout from './components/layout/PublicLayout';
import AdminLayout from './components/layout/AdminLayout';
import Home from './pages/Home';
import SinglePlayerGame from './pages/SinglePlayerGame';
import PublicGame from './pages/PublicGame';
import ControlRoom from './pages/ControlRoom';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import AdminPrizes from './pages/AdminPrizes';
import AdminGames from './pages/AdminGames';
import AdminUsers from './pages/AdminUsers';
import AdminLeaderboard from './pages/AdminLeaderboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Subscription from './pages/Subscription';
import LoadingSpinner from './components/ui/LoadingSpinner';

function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading, isAdmin, isOperator } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-bg-primary)',
      }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (!requireAdmin && !isOperator) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function GuestRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-bg-primary)',
      }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes - no auth required */}
      <Route path="/" element={<Home />} />
      <Route path="/play" element={<SinglePlayerGame />} />
      <Route element={<PublicLayout />}>
        <Route path="/live/:code" element={<PublicGame />} />
      </Route>

      {/* Auth */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <Login />
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <Register />
          </GuestRoute>
        }
      />

      {/* Protected: Control Room - requires auth (operator or admin) */}
      <Route
        path="/control"
        element={
          <ProtectedRoute>
            <ControlRoom />
          </ProtectedRoute>
        }
      />

      {/* Protected: Admin panel - requires operator or admin role */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="games" element={<AdminGames />} />
        <Route path="leaderboard" element={<AdminLeaderboard />} />
        <Route path="prizes" element={<AdminPrizes />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="subscription" element={<Subscription />} />
        {/* Users page: admin only */}
        <Route
          path="users"
          element={
            <ProtectedRoute requireAdmin>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <GameProvider>
          <AppRoutes />
        </GameProvider>
      </AuthProvider>
    </Router>
  );
}
